/**
 * Meet Your Guide Section
 *
 * Homepage section introducing the 6 guide characters.
 * Users can select their guide, which personalises their experience
 * throughout the app.
 *
 * Australian English throughout.
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Sparkles, MessageCircle, ArrowRight, X, ClipboardCheck, BookOpen } from 'lucide-react';
import { useGuide } from '../../context/GuideContext';
import { useApp } from '../../context/AppContext';
import { CHARACTER_ORDER } from '../../data/guideCharacters';

const MeetYourGuideSection = () => {
  const {
    allCharacters,
    selectedGuide,
    hasCompletedSelection,
    selectGuide
  } = useGuide();
  const { setCurrentView } = useApp();

  const [hoveredGuide, setHoveredGuide] = useState(null);
  const [showWelcome, setShowWelcome] = useState(false);
  const [justSelected, setJustSelected] = useState(null);

  const handleSelectGuide = (guideId) => {
    selectGuide(guideId);
    setJustSelected(guideId);
    setShowWelcome(true);
  };

  const handleCloseWelcome = () => {
    setShowWelcome(false);
    setJustSelected(null);
  };

  const handleStartAssessment = () => {
    setShowWelcome(false);
    setJustSelected(null);
    setCurrentView('vibe-assessment-intro');
  };

  const handleExploreModules = () => {
    setShowWelcome(false);
    setJustSelected(null);
    setCurrentView('modules');
  };

  return (
    <section className="py-16 md:py-24 bg-gradient-to-b from-slate-900 via-purple-900/20 to-slate-900">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/20 rounded-full mb-6">
            <Sparkles className="w-4 h-4 text-purple-400" />
            <span className="text-purple-300 text-sm font-medium">Personalised Support</span>
          </div>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            Meet Your Guide
          </h2>

          <p className="text-lg md:text-xl text-slate-300 max-w-2xl mx-auto">
            Choose someone who gets you. Your guide will be with you throughout your journey,
            offering support that matches your style.
          </p>

          {hasCompletedSelection && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-green-500/20 rounded-full"
            >
              <Check className="w-4 h-4 text-green-400" />
              <span className="text-green-300 text-sm">
                You're with <span className="font-semibold">{selectedGuide.name}</span> — tap another guide to switch
              </span>
            </motion.div>
          )}
        </motion.div>

        {/* Guide Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {CHARACTER_ORDER.map((guideId, index) => {
            const guide = allCharacters[guideId];
            const isSelected = hasCompletedSelection && selectedGuide.id === guideId;
            const isHovered = hoveredGuide === guideId;

            return (
              <motion.button
                key={guideId}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                onClick={() => handleSelectGuide(guideId)}
                onMouseEnter={() => setHoveredGuide(guideId)}
                onMouseLeave={() => setHoveredGuide(null)}
                className={`
                  relative p-6 rounded-2xl text-left transition-all duration-300
                  ${isSelected
                    ? 'bg-white/15 ring-2 ring-white/40'
                    : 'bg-white/5 hover:bg-white/10'
                  }
                  group cursor-pointer
                `}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {/* Selected Badge */}
                {isSelected && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -top-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center shadow-lg"
                    style={{ backgroundColor: guide.accentColor }}
                  >
                    <Check className="w-5 h-5 text-white" />
                  </motion.div>
                )}

                {/* Avatar */}
                <div className="flex items-start gap-4 mb-4">
                  <div
                    className="w-16 h-16 rounded-xl flex items-center justify-center text-2xl font-bold text-white shadow-lg transition-transform group-hover:scale-105"
                    style={{ backgroundColor: guide.accentColor }}
                  >
                    {guide.name[0]}
                  </div>

                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-1">
                      {guide.name}
                    </h3>
                    <p
                      className="text-sm font-medium"
                      style={{ color: guide.accentColor }}
                    >
                      {guide.tagline}
                    </p>
                  </div>
                </div>

                {/* Description */}
                <p className="text-slate-300 text-sm mb-4">
                  {guide.description}
                </p>

                {/* Sample Opening */}
                <div className="bg-black/20 rounded-lg p-3 mb-4">
                  <p className="text-slate-400 text-xs mb-1">They might say:</p>
                  <p className="text-white text-sm italic">
                    "{guide.personality.openings[0]}"
                  </p>
                </div>

                {/* Select CTA */}
                <div
                  className={`
                    flex items-center justify-center gap-2 py-2 px-4 rounded-lg
                    text-sm font-medium transition-all
                    ${isSelected
                      ? 'bg-white/20 text-white'
                      : 'text-white/70 group-hover:text-white'
                    }
                  `}
                  style={{
                    backgroundColor: isHovered && !isSelected ? `${guide.accentColor}30` : undefined
                  }}
                >
                  {isSelected ? (
                    <>
                      <MessageCircle className="w-4 h-4" />
                      <span>Your current guide</span>
                    </>
                  ) : (
                    <>
                      <span>Choose {guide.name}</span>
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </>
                  )}
                </div>
              </motion.button>
            );
          })}
        </div>

        {/* Reassurance */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center text-slate-400 text-sm mt-8"
        >
          You can change your guide anytime from settings. No wrong choices here.
        </motion.p>
      </div>

      {/* Welcome Modal */}
      <AnimatePresence>
        {showWelcome && justSelected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={handleCloseWelcome}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative max-w-lg w-full bg-slate-800 rounded-2xl p-6 shadow-2xl border border-white/10"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={handleCloseWelcome}
                className="absolute top-4 right-4 p-2 text-slate-400 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Guide Avatar */}
              <div className="flex justify-center mb-6">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', delay: 0.2 }}
                  className="w-20 h-20 rounded-2xl flex items-center justify-center text-3xl font-bold text-white shadow-xl"
                  style={{ backgroundColor: allCharacters[justSelected].accentColor }}
                >
                  {allCharacters[justSelected].name[0]}
                </motion.div>
              </div>

              {/* Welcome Message */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-center"
              >
                <h3 className="text-2xl font-bold text-white mb-4">
                  {allCharacters[justSelected].name} here! 👋
                </h3>

                <p className="text-slate-300 leading-relaxed mb-6">
                  {allCharacters[justSelected].welcomeMessage}
                </p>

                {/* What's Next */}
                <div className="bg-slate-700/50 rounded-xl p-4 mb-6 text-left">
                  <p className="text-white font-medium mb-3 text-center">What would you like to do first?</p>

                  <div className="space-y-3">
                    {/* Primary CTA - Assessment */}
                    <button
                      onClick={handleStartAssessment}
                      className="w-full flex items-center gap-3 p-3 rounded-lg text-white font-medium transition-all hover:scale-[1.02]"
                      style={{ backgroundColor: allCharacters[justSelected].accentColor }}
                    >
                      <ClipboardCheck className="w-5 h-5 flex-shrink-0" />
                      <div className="text-left">
                        <div className="font-semibold">Take the Friendship Vibe Check</div>
                        <div className="text-xs opacity-80">Discover your friendship style (10 mins)</div>
                      </div>
                      <ArrowRight className="w-5 h-5 ml-auto" />
                    </button>

                    {/* Secondary CTA - Modules */}
                    <button
                      onClick={handleExploreModules}
                      className="w-full flex items-center gap-3 p-3 rounded-lg bg-slate-600 text-white font-medium transition-all hover:bg-slate-500"
                    >
                      <BookOpen className="w-5 h-5 flex-shrink-0" />
                      <div className="text-left">
                        <div className="font-semibold">Explore the Modules</div>
                        <div className="text-xs opacity-80">Jump straight into learning</div>
                      </div>
                      <ArrowRight className="w-5 h-5 ml-auto" />
                    </button>
                  </div>
                </div>

                {/* Chat hint */}
                <div className="flex items-center justify-center gap-2 text-slate-400 text-sm">
                  <MessageCircle className="w-4 h-4" />
                  <span>
                    You can chat with {allCharacters[justSelected].name} anytime using the button in the corner
                  </span>
                </div>

                {/* Dismiss option */}
                <button
                  onClick={handleCloseWelcome}
                  className="mt-4 text-slate-500 text-sm hover:text-slate-300 transition-colors"
                >
                  I'll explore on my own first
                </button>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default MeetYourGuideSection;
